// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { Amplify, ConsoleLogger as Logger, parseAWSExports, StorageAction, } from '@aws-amplify/core';
import { AWSS3Provider } from '../providers';
import { isCancelError } from '../AwsClients/S3/utils';
import { AWSS3UploadTask } from '../providers/AWSS3UploadTask';
import { getStorageUserAgentValue } from '../common/StorageUtils';
var logger = new Logger('StorageClass');
var loggerStorageInstance = new Logger('Storage'); // Logging relating to Storage instance management
var DEFAULT_PROVIDER = 'AWSS3';
/**
 * Provide storage methods to use AWS S3
 */
var InternalStorageClass = /** @class */ (function () {
    /**
     * Initialize Storage
     * @param {Object} config - Configuration object for storage
     */
    function InternalStorageClass() {
        this._config = {};
        this._pluggables = [];
        this._abortControllerMap = new WeakMap();
        logger.debug('Storage Options', this._config);
        this.get = this.get.bind(this);
        this.put = this.put.bind(this);
        this.remove = this.remove.bind(this);
        this.list = this.list.bind(this);
    }
    InternalStorageClass.prototype.getModuleName = function () {
        return 'InternalStorage';
    };
    /**
     * add plugin into Storage category
     * @param {Object} pluggable - an instance of the plugin
     */
    InternalStorageClass.prototype.addPluggable = function (pluggable) {
        if (pluggable && pluggable.getCategory() === 'Storage') {
            this._pluggables.push(pluggable);
            var config = {};
            config = pluggable.configure(this._config[pluggable.getProviderName()]);
            return config;
        }
    };
    /**
     * Get the plugin object
     * @param providerName - the name of the plugin
     */
    InternalStorageClass.prototype.getPluggable = function (providerName) {
        var pluggable = this._pluggables.find(function (pluggable) { return pluggable.getProviderName() === providerName; });
        if (pluggable === undefined) {
            logger.debug('No plugin found with providerName', providerName);
            return null;
        }
        else
            return pluggable;
    };
    /**
     * Remove the plugin object
     * @param providerName - the name of the plugin
     */
    InternalStorageClass.prototype.removePluggable = function (providerName) {
        this._pluggables = this._pluggables.filter(function (pluggable) { return pluggable.getProviderName() !== providerName; });
        return;
    };
    /**
     * Configure Storage
     * @param {Object} config - Configuration object for storage
     * @return {Object} - Current configuration
     */
    InternalStorageClass.prototype.configure = function (config) {
        var _this = this;
        var _a;
        logger.debug('configure Storage');
        if (!config)
            return this._config;
        var amplifyConfig = parseAWSExports(config);
        var storageConfig = (_a = amplifyConfig.Storage) !== null && _a !== void 0 ? _a : {};
        var defaultProviderConfigKeys = [
            'bucket',
            'region',
            'level',
            'track',
            'customPrefix',
            'ContentMD5',
            'serverSideEncryption',
            'SSECustomerAlgorithm',
            'SSECustomerKey',
            // TODO(AllanZhengYP): remove in V6.
            'SSECustomerKeyMD5',
            'SSEKMSKeyId',
        ];
        var hasDefaultProviderConfigKeys = function (config) {
            return Object.keys(config).find(function (key) { return defaultProviderConfigKeys.includes(key); });
        };
        if (hasDefaultProviderConfigKeys(storageConfig) &&
            !storageConfig[DEFAULT_PROVIDER]) {
            storageConfig[DEFAULT_PROVIDER] = {};
        }
        Object.entries(storageConfig).forEach(function (_a) {
            var _b = __read(_a, 2), key = _b[0], value = _b[1];
            if (key &&
                defaultProviderConfigKeys.includes(key) &&
                value !== undefined) {
                storageConfig[DEFAULT_PROVIDER][key] = value;
                delete storageConfig[key];
            }
        });
        // only update new values for each provider
        Object.keys(storageConfig).forEach(function (providerName) {
            if (typeof storageConfig[providerName] !== 'string') {
                _this._config[providerName] = __assign(__assign({}, _this._config[providerName]), storageConfig[providerName]);
            }
        });
        this._pluggables.forEach(function (pluggable) {
            pluggable.configure(_this._config[pluggable.getProviderName()]);
        });
        if (this._pluggables.length === 0) {
            this.addPluggable(new AWSS3Provider());
        }
        return this._config;
    };
    InternalStorageClass.prototype.getAbortController = function () {
        return new AbortController();
    };
    InternalStorageClass.prototype.updateRequestToBeCancellable = function (request, abortController) {
        this._abortControllerMap.set(request, abortController);
    };
    InternalStorageClass.prototype.isUploadTask = function (x) {
        return (typeof x !== 'undefined' &&
            typeof x['pause'] === 'function' &&
            typeof x['resume'] === 'function');
    };
    /**
     * Cancels an inflight request
     *
     * @param request - The request to cancel
     * @param [message] - A message to include in the cancelation exception
     */
    InternalStorageClass.prototype.cancel = function (request, message, customUserAgentDetails) {
        if (request instanceof AWSS3UploadTask) {
            return request._cancel(getStorageUserAgentValue(StorageAction.Cancel, customUserAgentDetails));
        }
        var abortController = this._abortControllerMap.get(request);
        if (abortController) {
            // TODO: [v6] clean up the aborted promise in the weak map.
            // Not doing it yet to avoid breaking changes when users may abort a request twice.
            abortController.abort(message);
        }
        else {
            logger.debug('The request does not map to any cancel token');
        }
    };
    InternalStorageClass.prototype.copy = function (src, dest, config, customUserAgentDetails) {
        var provider = (config === null || config === void 0 ? void 0 : config.provider) || DEFAULT_PROVIDER;
        var plugin = this._pluggables.find(function (pluggable) { return pluggable.getProviderName() === provider; });
        if (plugin === undefined) {
            logger.debug('No plugin found with providerName', provider);
            return Promise.reject('No plugin found in Storage for the provider');
        }
        var abortController = this.getAbortController();
        if (typeof plugin.copy !== 'function') {
            return Promise.reject(".copy is not implemented on provider ".concat(plugin.getProviderName()));
        }
        var responsePromise = plugin.copy(src, dest, __assign(__assign({}, config), { abortSignal: abortController.signal }), getStorageUserAgentValue(StorageAction.Copy, customUserAgentDetails));
        this.updateRequestToBeCancellable(responsePromise, abortController);
        return responsePromise;
    };
    InternalStorageClass.prototype.get = function (key, config, customUserAgentDetails) {
        var provider = (config === null || config === void 0 ? void 0 : config.provider) || DEFAULT_PROVIDER;
        var plugin = this._pluggables.find(function (pluggable) { return pluggable.getProviderName() === provider; });
        if (plugin === undefined) {
            logger.debug('No plugin found with providerName', provider);
            return Promise.reject('No plugin found in Storage for the provider');
        }
        var abortController = this.getAbortController();
        var responsePromise = plugin.get(key, __assign(__assign({}, config), { abortSignal: abortController.signal }), getStorageUserAgentValue(StorageAction.Get, customUserAgentDetails));
        this.updateRequestToBeCancellable(responsePromise, abortController);
        return responsePromise;
    };
    InternalStorageClass.prototype.isCancelError = function (error) {
        return isCancelError(error);
    };
    InternalStorageClass.prototype.getProperties = function (key, config, customUserAgentDetails) {
        var provider = (config === null || config === void 0 ? void 0 : config.provider) || DEFAULT_PROVIDER;
        var plugin = this._pluggables.find(function (pluggable) { return pluggable.getProviderName() === provider; });
        if (plugin === undefined) {
            logger.debug('No plugin found with providerName', provider);
            throw new Error('No plugin found with providerName');
        }
        var abortController = this.getAbortController();
        if (typeof plugin.getProperties !== 'function') {
            return Promise.reject(".getProperties is not implemented on provider ".concat(plugin.getProviderName()));
        }
        var responsePromise = plugin === null || plugin === void 0 ? void 0 : plugin.getProperties(key, config, getStorageUserAgentValue(StorageAction.GetProperties, customUserAgentDetails));
        this.updateRequestToBeCancellable(responsePromise, abortController);
        return responsePromise;
    };
    InternalStorageClass.prototype.put = function (key, object, config, customUserAgentDetails) {
        var provider = (config === null || config === void 0 ? void 0 : config.provider) || DEFAULT_PROVIDER;
        var plugin = this._pluggables.find(function (pluggable) { return pluggable.getProviderName() === provider; });
        if (plugin === undefined) {
            logger.debug('No plugin found with providerName', provider);
            return Promise.reject('No plugin found in Storage for the provider');
        }
        var abortController = this.getAbortController();
        var response = plugin.put(key, object, __assign(__assign({}, config), { abortSignal: abortController.signal }), getStorageUserAgentValue(StorageAction.Put, customUserAgentDetails));
        if (!this.isUploadTask(response)) {
            this.updateRequestToBeCancellable(response, abortController);
        }
        return response;
    };
    InternalStorageClass.prototype.remove = function (key, config, customUserAgentDetails) {
        var provider = (config === null || config === void 0 ? void 0 : config.provider) || DEFAULT_PROVIDER;
        var plugin = this._pluggables.find(function (pluggable) { return pluggable.getProviderName() === provider; });
        if (plugin === undefined) {
            logger.debug('No plugin found with providerName', provider);
            return Promise.reject('No plugin found in Storage for the provider');
        }
        return plugin.remove(key, config, getStorageUserAgentValue(StorageAction.Remove, customUserAgentDetails));
    };
    InternalStorageClass.prototype.list = function (path, config, customUserAgentDetails) {
        var provider = (config === null || config === void 0 ? void 0 : config.provider) || DEFAULT_PROVIDER;
        var plugin = this._pluggables.find(function (pluggable) { return pluggable.getProviderName() === provider; });
        if (plugin === undefined) {
            logger.debug('No plugin found with providerName', provider);
            return Promise.reject('No plugin found in Storage for the provider');
        }
        return plugin.list(path, config, getStorageUserAgentValue(StorageAction.List, customUserAgentDetails));
    };
    return InternalStorageClass;
}());
export { InternalStorageClass };
export var InternalStorage = new InternalStorageClass();
Amplify.register(InternalStorage);
